import type { ReactNode } from 'react';
import { Fragment } from 'react';

import type { ApiListingLocationType } from 'api/types/ApiListingLocationType';
import type { ApiISODateTimeString } from 'api/types/ApiTypedDate';
import { Box } from 'components/Box/Box';
import { LegacyTooltip } from 'components/LegacyTooltip/LegacyTooltip';
import { ListingTypeLabel } from 'modules/listing/components/ListingTypeLabel/ListingTypeLabel';
import { SearchHitInfoBoxListingLabels } from 'modules/search/components/Hit/Info/ListingLabels/SearchHitInfoBoxListingLabels';
import {
  SearchHitInfoFooterHolder,
  SearchHitInfoHeader,
  SearchHitInfoHeaderHeading,
  SearchHitInfoHeaderOrgName,
  SearchHitInfoHolder,
  SearchHitInfoPipe,
  SearchHitInfoStats,
} from 'modules/search/components/Hit/Info/SearchHitInfo.styled';
import { SearchHitApplicationStatus } from 'modules/search/components/Hit/SearchHitApplicationStatus';

type Props = {
  application?: {
    status: 'ACTIVE' | 'INACTIVE';
    created: ApiISODateTimeString;
  };
  isPostedAnonymously?: boolean;
  location?: string;
  locationType?: ApiListingLocationType;
  listingStats: ReactNode[];
  name: string;
  orgName?: string | null;
  orgType?: string | null;
  showLabel?: boolean;
};

export function SearchInternshipHitInfo({
  application,
  isPostedAnonymously,
  location,
  locationType,
  listingStats,
  name,
  orgName,
  orgType,
  showLabel,
}: Props) {
  const type = 'INTERNSHIP';

  return (
    <SearchHitInfoHolder>
      {showLabel && (
        <div style={{ marginBottom: 14 }}>
          <ListingTypeLabel listingType={type} />
        </div>
      )}

      <SearchHitInfoHeader>
        <SearchHitInfoHeaderHeading>
          <div>
            <span data-qa-id="search-result-link">{name.slice(0, 120)}</span>
            {name.length > 120 && <>&hellip;</>}
          </div>

          {isPostedAnonymously && (
            <Box display="inline-block" ml="4px" verticalAlign="middle">
              <LegacyTooltip width={360}>
                {getText(
                  `The hiring organization has chosen to publish this listing anonymously to maintain confidentiality during the hiring process. They have provided Idealist with all of their organization’s details and currently meet our posting guidelines.`,
                )}
              </LegacyTooltip>
            </Box>
          )}
        </SearchHitInfoHeaderHeading>

        {(orgType === 'CORPORATION' || orgName) && (
          <SearchHitInfoHeaderOrgName>
            <Box display="flex" alignItems="center" flexWrap="wrap">
              {orgName}
            </Box>
          </SearchHitInfoHeaderOrgName>
        )}
      </SearchHitInfoHeader>

      <SearchHitInfoBoxListingLabels
        listingType={type}
        location={location}
        locationType={locationType}
      />

      <SearchHitInfoFooterHolder>
        {listingStats.length > 0 && (
          <SearchHitInfoStats>
            {listingStats.map((info, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <Fragment key={index}>
                {index > 0 && <SearchHitInfoPipe />}
                {info}
              </Fragment>
            ))}
          </SearchHitInfoStats>
        )}

        {application && (
          <SearchHitApplicationStatus
            status={application.status}
            created={application.created}
          />
        )}
      </SearchHitInfoFooterHolder>
    </SearchHitInfoHolder>
  );
}
